'use client';

import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { ProgressBar as AriaProgressBar, Label } from 'react-aria-components';
import type { ProgressBarProps } from 'react-aria-components';

import { useFusionTheme } from '../hooks';

import styles from './ProgressBar.module.scss';

export type Props = Omit<
  ProgressBarProps,
  'isIndeterminate' | 'formatOptions' | 'valueLabel' | 'children' | 'style'
> &
  VariantProps<typeof progressBarVariants> & {
    leftLabel?: string;
    rightLabel?: string;
  };

const progressBarVariants = cva(styles.container, {
  variants: {
    variant: {
      active: styles.variantActive,
      warning: styles.variantWarning,
      error: styles.variantError
    }
  }
});

const ProgressBar = ({ leftLabel, rightLabel, className, variant = 'active', ...props }: Props) => {
  const { addThemeClassName } = useFusionTheme();

  return (
    <AriaProgressBar
      {...props}
      className={addThemeClassName(progressBarVariants({ variant }), className)}
    >
      {({ percentage }) => (
        <>
          <div className={styles.bar}>
            <div className={styles.fill} style={{ width: percentage + '%' }} />
          </div>
          {leftLabel && rightLabel && (
            <div className={styles.labelContainer}>
              <Label>{leftLabel}</Label>
              <Label>{rightLabel}</Label>
            </div>
          )}
        </>
      )}
    </AriaProgressBar>
  );
};

export default ProgressBar;
